/**
 * #5068 — the drawing program API. A program sets the card, then runs beats in order; inside a beat it places kit
 * parts and declares joints. Signaling's sandbox (Phase 2) exposes exactly these calls to model-written programs, one
 * top-level statement at a time through `createDrawingProgram`; samplePrograms.ts holds hand-written ones.
 *
 * Nothing a program passes is trusted. Numbers are clamped to their ranges (a model's `size: 1000` draws at the
 * largest size, it does not lose the drawing), unknown fields are dropped, and a mistake is reported at the call that
 * made it, naming the part or beat. With `dropFailedBeats`, a beat that fails is removed whole and the program goes
 * on; `onBeat` sees the drawing after each completed beat, so it can be published while the rest is still coming.
 */
import { z } from 'zod';
import { ContextualVisualDrawingSchema, DRAWING_COLOR_ROLES, DRAWING_HEIGHT, DRAWING_MAX_BEATS, DRAWING_MAX_JOINTS, DRAWING_MAX_PARTS, DRAWING_WIDTH,
  DrawingBeatSchema, DrawingJointSchema, DrawingPartSchema, type ContextualVisualDrawing, type DrawingJoint, type DrawingPart } from './DrawingSchema';
import { isAbsoluteDrawingKit, isDrawingKitName } from './drawingParts';
import { DrawingPlacementError, resolveDrawingPlacement, settleDrawingBeat, type DrawingPlacement } from './drawingPlacement';
import { DRAWING_MAX_TRIANGLES, buildPart3d, meshTriangles } from './partsKit3d';
import { placeSketchPart } from './sketchKit';
import { seededRandom } from './sketchStroke';

export type DrawingPartInput = Partial<Pick<DrawingPart, 'params' | 'x' | 'y' | 'z' | 'size' | 'rotate' | 'tilt' | 'yaw' | 'label' | 'explanation' | 'color'>> & DrawingPlacement;
export interface DrawingProgramApi {
  card: (title: string, details?: { subtitle?: string; takeaway?: string; period?: number; view?: '2d' | '3d' }) => void;
  beat: (id: string, sentence: string, draw: () => void) => void;
  part: (kit: string, id: string, input?: DrawingPartInput) => void;
  joint: (joint: DrawingJoint) => void;
  /** Seeded, so a program that uses it draws the same every time (D5). */
  random: () => number;
}
export class DrawingProgramError extends Error {}
export interface DrawingProgramOptions {
  onBeat?: (drawing: ContextualVisualDrawing) => void;
  /** Remove a beat that fails, with everything it drew, and carry on (D8); errors are reported in `errors`. */
  dropFailedBeats?: boolean;
  errors?: string[];
  /** What the layout check moved, beat by beat (for logs and the benchmark). */
  layout?: string[];
}
/** The most a drawing may draw after its parts are expanded (spec: 800 primitives). */
export const DRAWING_MAX_STROKES = 800;

const clamp = (value: unknown, min: number, max: number, fallback: number) =>
  typeof value === 'number' && Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : fallback;
const text = (value: unknown, max: number) => typeof value === 'string' ? value.slice(0, max) : '';
/**
 * A beat's sentence is spoken word for word: control and invisible formatting characters become spaces, and bracketed
 * tags (which a voice may read as delivery directions, #5014) are removed.
 */
const spoken = (value: unknown) => typeof value === 'string'
  ? value.replace(/[\p{Cc}\p{Cf}]/gu, ' ').replace(/\[[^\]]*\]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 240) : '';
/** Joint numbers clamped to the schema's ranges before validation. */
const JOINT_RANGES: Record<string, [number, number]> = { ratio: [-8, 8], phase: [0, 1], dx: [-400, 400], dy: [-400, 400], radius: [2, 300],
  rodLength: [4, 800], axis: [-360, 360], lift: [0, 200], center: [0, 1], width: [.05, 1] };

/** A program being run call by call: the sandbox (Phase 2) drives `api` one top-level statement at a time. */
export interface DrawingProgramBuilder {
  api: DrawingProgramApi;
  /** The drawing so far, or null while it is not yet a valid drawing (no beat has drawn a part). */
  drawing: () => ContextualVisualDrawing | null;
  /** The finished drawing. Throws DrawingProgramError when it is not a valid drawing. */
  finish: () => ContextualVisualDrawing;
}

/** Runs `program` against a fresh API and returns the validated drawing. Throws DrawingProgramError on a bad call. */
export function runDrawingProgram(program: (api: DrawingProgramApi) => void, options: DrawingProgramOptions = {}): ContextualVisualDrawing {
  const builder = createDrawingProgram(options);
  program(builder.api);
  return builder.finish();
}

/** A fresh, empty program: every call validates and clamps its arguments as described above. */
export function createDrawingProgram(options: DrawingProgramOptions = {}): DrawingProgramBuilder {
  let title = '', subtitle = '', takeaway = '', period = 4, currentBeat: string | null = null, strokes = 0, triangles = 0;
  let view: '2d' | '3d' = '2d';
  const beats: ContextualVisualDrawing['beats'] = [], parts: DrawingPart[] = [], joints: DrawingJoint[] = [];
  const random = seededRandom(0x5068);
  // Parts placed relative to others: who is related to whom, whose position follows whose, and arrows to re-aim.
  const related = new Map<string, Set<string>>(), children = new Map<string, string[]>(), betweens = new Map<string, DrawingPlacement>();
  const relate = (a: string, b: string) => { for (const [x, y] of [[a, b], [b, a]]) { const set = related.get(x) ?? new Set(); set.add(y); related.set(x, set); } };
  const snapshot = (): ContextualVisualDrawing | null => {
    const result = ContextualVisualDrawingSchema.safeParse({ title: title || 'Drawing', subtitle, takeaway, beats, parts, joints, period, view });
    return result.success ? result.data : null;
  };
  const api: DrawingProgramApi = {
    random,
    card: (value, details = {}) => {
      title = text(value, 100) || 'Drawing'; subtitle = text(details?.subtitle, 80); takeaway = text(details?.takeaway, 80);
      period = clamp(details?.period, 1, 30, period);
      if (details?.view === '2d' || details?.view === '3d') {
        if (parts.length && details.view !== view) throw new DrawingProgramError('Choose the view in card() before drawing any part');
        view = details.view;
      }
    },
    beat: (id, sentence, draw) => {
      if (currentBeat) throw new DrawingProgramError(`Beat "${id}" is inside beat "${currentBeat}"; beats cannot be nested`);
      const beat = DrawingBeatSchema.safeParse({ id, sentence: spoken(sentence) });
      if (!beat.success) throw new DrawingProgramError(`Beat "${String(id).slice(0, 40)}": needs an id (letters, digits, - or _) and a sentence`);
      if (beats.some(item => item.id === id)) throw new DrawingProgramError(`Beat "${id}" is used twice`);
      if (beats.length >= DRAWING_MAX_BEATS) throw new DrawingProgramError(`At most ${DRAWING_MAX_BEATS} beats`);
      const marks = { parts: parts.length, joints: joints.length, strokes, triangles };
      beats.push(beat.data);
      currentBeat = beat.data.id;
      try {
        draw();
      } catch (error) {
        if (!options.dropFailedBeats) throw error;
        // Remove the whole beat, as if it had never been written.
        beats.pop(); parts.length = marks.parts; joints.length = marks.joints; strokes = marks.strokes; triangles = marks.triangles;
        options.errors?.push(error instanceof Error ? error.message : String(error));
        return;
      } finally { currentBeat = null; }
      // The layout check: this beat's parts on the sheet and off parts they do not belong to; arrows re-aimed.
      const jointed = new Set(joints.flatMap(joint => Object.values(joint).filter((value): value is string => typeof value === 'string' && parts.some(part => part.id === value))));
      const settled = settleDrawingBeat(parts, marks.parts, (a, b) => !!related.get(a)?.has(b), jointed, id => children.get(id) ?? []);
      if (settled.notes.length) {
        parts.splice(0, parts.length, ...settled.parts);
        for (let index = marks.parts; index < parts.length; index++) {
          const placement = betweens.get(parts[index].id);
          if (!placement) continue;
          try { const resolved = resolveDrawingPlacement(placement, parts[index], parts); parts[index] = { ...parts[index], params: (resolved.params ?? parts[index].params) as DrawingPart['params'] }; } catch { /* keep its ends */ }
        }
        options.layout?.push(...settled.notes);
      }
      const drawing = snapshot();
      if (drawing) options.onBeat?.(drawing);
    },
    part: (kit, id, input = {}) => {
      if (!currentBeat) throw new DrawingProgramError(`Part "${id}" must be drawn inside a beat`);
      if (!isDrawingKitName(kit, view)) throw new DrawingProgramError(`Part "${id}": unknown ${view} kit item "${String(kit).slice(0, 40)}"`);
      if (parts.some(part => part.id === id)) throw new DrawingProgramError(`Part "${id}" is used twice`);
      if (parts.length >= DRAWING_MAX_PARTS) throw new DrawingProgramError(`At most ${DRAWING_MAX_PARTS} parts`);
      const values = input ?? {};
      const parsed = DrawingPartSchema.safeParse({
        id, kit, beat: currentBeat,
        // Non-finite numbers (1/0 in a program) would not survive JSON: drop them, and the kit uses its default.
        params: typeof values.params === 'object' && values.params
          ? Object.fromEntries(Object.entries(values.params).filter(([, value]) => typeof value !== 'number' || Number.isFinite(value))) : {},
        x: clamp(values.x, -DRAWING_WIDTH, DRAWING_WIDTH * 2, DRAWING_WIDTH / 2), y: clamp(values.y, -DRAWING_HEIGHT, DRAWING_HEIGHT * 2, DRAWING_HEIGHT / 2),
        size: clamp(values.size, 4, DRAWING_WIDTH, 120), rotate: clamp(values.rotate, -720, 720, 0),
        z: clamp(values.z, -DRAWING_WIDTH, DRAWING_WIDTH, 0), tilt: clamp(values.tilt, -360, 360, 0), yaw: clamp(values.yaw, -360, 360, 0),
        label: text(values.label, 60), explanation: text(values.explanation, 240),
        color: (DRAWING_COLOR_ROLES as readonly string[]).includes(values.color as string) ? values.color : 'ink',
      });
      if (!parsed.success) throw new DrawingProgramError(`Part "${id}": ${parsed.error.issues[0]?.path.join('.')} ${parsed.error.issues[0]?.message ?? 'invalid'}`);
      let placedPart = parsed.data;
      const placement: DrawingPlacement = { inside: values.inside, at: values.at, beside: values.beside, between: values.between };
      if (placement.inside || placement.at || placement.beside || placement.between) {
        let resolved: ReturnType<typeof resolveDrawingPlacement>;
        try { resolved = resolveDrawingPlacement(placement, placedPart, parts); }
        catch (error) { throw new DrawingProgramError(error instanceof DrawingPlacementError ? error.message : `Part "${id}": could not be placed`); }
        const again = DrawingPartSchema.safeParse({ ...placedPart,
          x: clamp(resolved.x ?? placedPart.x, -DRAWING_WIDTH, DRAWING_WIDTH * 2, placedPart.x), y: clamp(resolved.y ?? placedPart.y, -DRAWING_HEIGHT, DRAWING_HEIGHT * 2, placedPart.y),
          size: clamp(resolved.size ?? placedPart.size, 4, DRAWING_WIDTH, placedPart.size), rotate: clamp(resolved.rotate ?? placedPart.rotate, -720, 720, placedPart.rotate),
          params: resolved.params ?? placedPart.params });
        if (!again.success) throw new DrawingProgramError(`Part "${id}": could not be placed (${again.error.issues[0]?.message ?? 'invalid'})`);
        placedPart = again.data;
        for (const parent of resolved.parents) {
          relate(id, parent);
          if (!placement.between) children.set(parent, [...(children.get(parent) ?? []), id]);
        }
        if (placement.between && isAbsoluteDrawingKit(kit)) betweens.set(id, placement);
      }
      if (view === '3d') {
        const built = meshTriangles(buildPart3d(placedPart));
        if (triangles + built > DRAWING_MAX_TRIANGLES) throw new DrawingProgramError(`Part "${id}": the model is too detailed (at most ${DRAWING_MAX_TRIANGLES} triangles)`);
        triangles += built;
      } else {
        const drawn = placeSketchPart(placedPart)?.strokes.length ?? 0;
        if (strokes + drawn > DRAWING_MAX_STROKES) throw new DrawingProgramError(`Part "${id}": the drawing is too detailed (at most ${DRAWING_MAX_STROKES} strokes)`);
        strokes += drawn;
      }
      parts.push(placedPart);
    },
    joint: joint => {
      if (joints.length >= DRAWING_MAX_JOINTS) throw new DrawingProgramError(`At most ${DRAWING_MAX_JOINTS} joints`);
      const values = Object.fromEntries(Object.entries(joint ?? {}).map(([key, value]) =>
        [key, JOINT_RANGES[key] ? clamp(value, JOINT_RANGES[key][0], JOINT_RANGES[key][1], NaN) : value]).filter(([, value]) => !Number.isNaN(value)));
      const parsed = DrawingJointSchema.safeParse(values);
      if (!parsed.success) throw new DrawingProgramError(`Joint ${String((joint as { kind?: unknown })?.kind ?? '?').slice(0, 20)}: ${parsed.error.issues[0]?.path.join('.')} ${parsed.error.issues[0]?.message ?? 'invalid'}`);
      const value = parsed.data;
      const refs = value.kind === 'rotate' || value.kind === 'slide' ? [value.part] : value.kind === 'crankSlider' ? [value.crank, value.slider, ...(value.link ? [value.link] : [])]
        : value.kind === 'cam' ? [value.cam, value.follower] : value.kind === 'gear' ? [value.driver, value.driven] : [value.part, value.follow];
      const missing = refs.find(ref => !parts.some(part => part.id === ref));
      if (missing) throw new DrawingProgramError(`Joint ${value.kind}: no part "${missing}" (draw it first)`);
      const kitOf = (ref: string) => parts.find(part => part.id === ref)!.kit;
      // Only a drawn shape can turn; a part drawn from sheet points (an arrow, a rod) can only move or follow.
      const turning = value.kind === 'rotate' ? [value.part] : value.kind === 'crankSlider' ? [value.crank] : value.kind === 'cam' ? [value.cam]
        : value.kind === 'gear' ? [value.driver, value.driven] : [];
      const flat = turning.find(ref => isAbsoluteDrawingKit(kitOf(ref)));
      if (flat) throw new DrawingProgramError(`Joint ${value.kind}: "${flat}" is drawn from sheet points and cannot turn`);
      // A part that turns about its axle must face the viewer: joints spin it in the sheet's plane.
      const leaning = turning.find(ref => { const target = parts.find(part => part.id === ref)!; return target.tilt !== 0 || target.yaw !== 0; });
      if (leaning) throw new DrawingProgramError(`Joint ${value.kind}: "${leaning}" is tilted or turned; a part that spins must face the viewer`);
      if (value.kind === 'attach' && !isAbsoluteDrawingKit(kitOf(value.part))) throw new DrawingProgramError(`Joint attach: "${value.part}" has no ends to attach (use a spring, link, wire, pipe or arrow)`);
      if (value.kind === 'crankSlider' && value.link && !isAbsoluteDrawingKit(kitOf(value.link))) throw new DrawingProgramError(`Joint crankSlider: the link "${value.link}" must be a link`);
      joints.push(value);
    },
  };
  return {
    api,
    drawing: snapshot,
    finish: () => {
      const drawing = snapshot();
      if (!drawing) {
        const result = ContextualVisualDrawingSchema.safeParse({ title: title || 'Drawing', subtitle, takeaway, beats, parts, joints, period });
        throw new DrawingProgramError(result.success ? 'Invalid drawing' : result.error.issues.map(issue => `${issue.path.join('.')}: ${issue.message}`).join('; '));
      }
      return drawing;
    },
  };
}
